import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import styled from 'styled-components';

import fontService, { FontWeight } from 'services/FontService';
import { SkillIcon } from 'atoms/SkillIcon/SkillIcon';
import { tablet } from 'services/ui';
import {
  CAREER_SKILL_SOURCE_COURSE,
  CAREER_SKILL_SOURCE_SELF,
  Colors,
  ICareerSkillValue,
} from 'context';
import { ICareerSkillOption } from 'core';
import { Icon, IconNames } from 'atoms';
import { InputWithTags, SkillAutocomplete, SkillLevelChoice } from 'molecules';
import { careerColor } from 'services/careerTokens';
import { removeLabel } from 'services/removeLabel';
import { careerLayout, careerType } from 'services/careerType';
import { FormFieldset } from 'organisms/ResumeForm';

/** Навык словаря, упомянутый в CV, но ещё без уровня (0072). */
export interface ICvSkillSuggestion {
  mentionedAs: string;
  skill: { id: string; slug: string; name: string; category: string; iconUrl: string | null };
}

export interface IProfileSkillsBlock {
  skills: ICareerSkillValue[];
  customSkills: string[];
  onChangeSkills: (skills: ICareerSkillValue[]) => void;
  onChangeCustomSkills: (customSkills: string[]) => void;
  /** «Wspomniano w CV» — с сервера, по последнему сохранению. */
  suggestions?: ICvSkillSuggestion[];
}

const DEFAULT_LEVEL = 3;

/**
 * Подпись значка «подтверждено» (ТЗ 0040).
 *
 * Три случая, и они правда разные: навык за пройденное занятие, навык за
 * курс целиком, и подтверждение без названия курса (курс удалили — FK стоит
 * `SET_NULL`, и строка обязана остаться читаемой).
 */
const verifiedTitle = (t: TFunction, skill: ICareerSkillValue): string => {
  if (!skill.grantedCourseName) return t('careerProfile.skills.verified');
  if (skill.grantedLessonNumber) {
    return t('careerProfile.skills.verifiedByLesson', {
      n: skill.grantedLessonNumber,
      course: skill.grantedCourseName,
    });
  }
  return t('careerProfile.skills.verifiedByCourse', {
    course: skill.grantedCourseName,
  });
};

/**
 * Skills section (spec 0018, increment B). Replaces free-text entry with a
 * dictionary autocomplete: each picked skill gets a 1-5 level. A typed value that
 * matches no dictionary skill drops into the free-text `customSkills` tail.
 *
 * ТЗ 0039 — навыки «подтверждено школой». У такой строки:
 *
 * * есть значок с названием курса — это утверждение ШКОЛЫ, и оно ценно
 *   работодателю именно тем, что студент не мог поставить его себе сам
 *   (поля `source` нет во входном типе мутации);
 * * вместо крестика — переключатель «скрыть из резюме». Удалять нельзя: строку
 *   ставил не студент, и снос её означал бы, что человек может стереть факт
 *   прохождения курса. Скрытая строка не идёт ни в PDF, ни в подбор вакансий;
 * * уровень править МОЖНО. Это его собственная оценка, и начисление её не
 *   перетирает (см. `services/skill_grant.py`).
 */
export const ProfileSkillsBlock = memo(
  ({
    skills,
    customSkills,
    onChangeSkills,
    onChangeCustomSkills,
    suggestions = [],
  }: IProfileSkillsBlock) => {
    const { t } = useTranslation();

    /**
     * 0072: оценка упомянутого навыка — обычная правка формы. Навык встаёт в
     * список со своим уровнем, текст уходит из свободных, и всё сохраняется
     * той же «Zapisz zmiany». Отдельная мутация здесь затёрлась бы следующим
     * сохранением формы, где этого навыка ещё нет.
     */
    const onRateSuggestion = useCallback(
      (suggestion: ICvSkillSuggestion, level: number) => {
        if (skills.some((skill) => skill.skillId === suggestion.skill.id)) return;
        onChangeSkills([
          ...skills,
          {
            skillId: suggestion.skill.id,
            name: suggestion.skill.name,
            slug: suggestion.skill.slug,
            iconUrl: suggestion.skill.iconUrl,
            category: suggestion.skill.category,
            level,
            years: null,
            source: CAREER_SKILL_SOURCE_SELF,
            isHidden: false,
            grantedCourseName: null,
            grantedLessonNumber: null,
          },
        ]);
        const mentioned = suggestion.mentionedAs.trim().toLowerCase();
        onChangeCustomSkills(
          customSkills.filter((text) => text.trim().toLowerCase() !== mentioned),
        );
      },
      [skills, customSkills, onChangeSkills, onChangeCustomSkills],
    );

    const openSuggestions = suggestions.filter(
      (suggestion) => !skills.some((skill) => skill.skillId === suggestion.skill.id),
    );

    const onSelectSkill = useCallback(
      (option: ICareerSkillOption) => {
        if (skills.some((skill) => skill.skillId === option.id)) {
          return;
        }
        onChangeSkills([
          ...skills,
          {
            skillId: option.id,
            name: option.name,
            slug: option.slug,
            iconUrl: option.iconUrl,
            category: option.category,
            level: DEFAULT_LEVEL,
            years: null,
            source: CAREER_SKILL_SOURCE_SELF,
            isHidden: false,
            grantedCourseName: null,
            grantedLessonNumber: null,
          },
        ]);
      },
      [skills, onChangeSkills],
    );

    const onFreeText = useCallback(
      (text: string) => {
        if (customSkills.some((skill) => skill.toLowerCase() === text.toLowerCase())) {
          return;
        }
        onChangeCustomSkills([...customSkills, text]);
      },
      [customSkills, onChangeCustomSkills],
    );

    const onChangeLevel = useCallback(
      (skillId: string, level: number) => {
        onChangeSkills(
          skills.map((skill) =>
            skill.skillId === skillId ? { ...skill, level: level || 1 } : skill,
          ),
        );
      },
      [skills, onChangeSkills],
    );

    const onRemoveSkill = useCallback(
      (skillId: string) => {
        onChangeSkills(skills.filter((skill) => skill.skillId !== skillId));
      },
      [skills, onChangeSkills],
    );

    /** Скрыть/вернуть подтверждённый навык. Удаления для него нет. */
    const onToggleHidden = useCallback(
      (skillId: string) => {
        onChangeSkills(
          skills.map((skill) =>
            skill.skillId === skillId ? { ...skill, isHidden: !skill.isHidden } : skill,
          ),
        );
      },
      [skills, onChangeSkills],
    );

    return (
      <FormFieldset groupTitle={t('careerProfile.skills.title')}>
        <SkillAutocomplete
          placeholder={t('careerProfile.skills.searchPlaceholder')}
          onSelectSkill={onSelectSkill}
          onFreeText={onFreeText}
        />

        <SkillList>
          {skills.map((skill) => {
            const isVerified = skill.source === CAREER_SKILL_SOURCE_COURSE;
            return (
              <SkillRow key={skill.skillId} $dimmed={skill.isHidden}>
                <SkillName>
                  {/* Тот же знак, что на витрине резюме и в карте навыков:
                      редактор и предпросмотр одного CV не имеют права
                      показывать один навык двумя разными вещами. Данные для
                      знака в строке уже лежали — их выбрасывал показ. */}
                  <SkillIcon
                    slug={skill.slug}
                    name={skill.name}
                    category={skill.category}
                    iconUrl={skill.iconUrl}
                    size={18}
                  />
                  {skill.name}
                  {isVerified && (
                    <VerifiedBadge title={verifiedTitle(t, skill)}>
                      {t('careerProfile.skills.verifiedShort')}
                    </VerifiedBadge>
                  )}
                </SkillName>
                <LevelCell>
                  {/* Источник оценки словами: своя оценка ≠ подтверждение школы. */}
                  <SkillLevelChoice
                    skillName={skill.name}
                    value={skill.level}
                    onChange={(level) => onChangeLevel(skill.skillId, level)}
                    hint={
                      isVerified
                        ? t('careerCenter.skillSource.course')
                        : t('careerCenter.skillSource.self')
                    }
                  />
                </LevelCell>
                {isVerified ? (
                  // Текстовая кнопка, а не иконка: значка «скрыть» в наборе
                  // нет, а натягивать на смысл чужую (закладку, крестик)
                  // значит сказать пользователю неправду о том, что будет.
                  <HideButton type="button" onClick={() => onToggleHidden(skill.skillId)}>
                    {t(
                      skill.isHidden
                        ? 'careerProfile.skills.showInCv'
                        : 'careerProfile.skills.hideFromCv',
                    )}
                  </HideButton>
                ) : (
                  <RemoveButton
                    type="button"
                    aria-label={removeLabel(t, 'careerProfile.remove.skill', skill.name)}
                    onClick={() => onRemoveSkill(skill.skillId)}>
                    <Icon name={IconNames.GrayCloseSm} width={20} height={20} />
                  </RemoveButton>
                )}
              </SkillRow>
            );
          })}
        </SkillList>

        {openSuggestions.length ? (
          <Suggestions aria-labelledby="cv-skill-suggestions-title">
            <SuggestionsTitle id="cv-skill-suggestions-title">
              {t('careerProfile.skills.suggestionsTitle')}
            </SuggestionsTitle>
            <SuggestionsHint>{t('careerProfile.skills.suggestionsHint')}</SuggestionsHint>
            {openSuggestions.map((suggestion) => (
              <SuggestionRow key={suggestion.skill.id}>
                <SkillName>
                  <SkillIcon
                    slug={suggestion.skill.slug}
                    name={suggestion.skill.name}
                    category={suggestion.skill.category}
                    iconUrl={suggestion.skill.iconUrl}
                    size={18}
                  />
                  {suggestion.skill.name}
                </SkillName>
                <LevelCell>
                  <SkillLevelChoice
                    skillName={suggestion.skill.name}
                    value={null}
                    onChange={(level) => onRateSuggestion(suggestion, level)}
                    hint={t('careerCenter.skillSource.mentioned')}
                  />
                </LevelCell>
              </SuggestionRow>
            ))}
          </Suggestions>
        ) : null}

        <CustomLabel>{t('careerProfile.skills.customLabel')}</CustomLabel>
        <InputWithTags
          tagsList={customSkills}
          placeholder={t('careerProfile.skills.customPlaceholder')}
          onChange={onChangeCustomSkills}
        />
      </FormFieldset>
    );
  },
);

const SkillList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0 24px;
`;

const SkillRow = styled.div<{ $dimmed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.45 : 1)};
`;

/** Значок «подтверждено школой». Подпись курса — в title, чтобы длинное
 *  название не ломало строку на мобильном. */
const VerifiedBadge = styled.span`
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 10px;
  white-space: nowrap;
  background-color: ${Colors.main};
  color: ${Colors.btn_text};
  ${fontService.text({ size: 11, lineHeight: 145, weight: FontWeight.medium })}
`;

const SkillName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 160px;
  ${fontService.text({ size: 15, lineHeight: 133, weight: FontWeight.medium })}

  ${tablet} {
    min-width: 200px;
  }
`;

const RemoveButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;

  &:hover svg > * {
    transition: 0.3s;
    fill: ${Colors.black};
  }
`;

const HideButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  color: ${Colors.gray_dark};
  ${fontService.text({ size: 13, lineHeight: 145, weight: FontWeight.medium })}

  &:hover {
    color: ${Colors.black};
  }
`;

const LevelCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 260px;
  min-width: 0;
`;

const Suggestions = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0 0 24px;
  padding: 16px;
  border: 1px dashed ${careerColor.line};
  border-radius: ${careerLayout.controlRadius}px;
`;

const SuggestionsTitle = styled.h4`
  margin: 0;
  color: ${careerColor.text};
  ${careerType.bodyStrong()};
`;

const SuggestionsHint = styled.p`
  margin: -8px 0 0;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const SuggestionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

const CustomLabel = styled.p`
  margin-bottom: 4px;
  ${fontService.text({ size: 14, lineHeight: 145, weight: FontWeight.medium })}
`;
